import React, { useState, useEffect } from 'react';
import { Outlet, useParams, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MatchContext from '../../contexts/MatchContext';
import { getMatch, getAnnouncements, getMatchAuth } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import ALL_LANGUAGES from '../../i18n/languages';

const LANGUAGES = ALL_LANGUAGES.map((l) => ({ code: l.locale, label: l.label }));

export default function MatchViewerWrapper({ locale, onLocaleChange }) {
  const { matchId } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [match, setMatch] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [langAnchor, setLangAnchor] = useState(null);
  const [serverVersion, setServerVersion] = useState('...');

  useEffect(() => {
    const base = import.meta.env.VITE_API_URL || '';
    fetch(`${base}/api/version`).then(r => r.json()).then(d => setServerVersion(d.version || '?')).catch(() => setServerVersion('?'));
  }, []);

  const fetchMatch = async () => {
    try {
      setLoading(true);
      setError(null);

      const matchRes = await getMatch(matchId);
      setMatch(matchRes.data);

      try {
        const announcementsRes = await getAnnouncements(matchId);
        setAnnouncements(announcementsRes.data || []);
      } catch {
        // Announcements are optional
      }

      if (user) {
        try {
          const authRes = await getMatchAuth(matchId);
          setAuth(authRes.data);
        } catch {
          // Auth check is optional for viewers
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Match not found');
      } else {
        setError('Failed to load match data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatch();
  }, [matchId, user]);

  // Auto-set language from match locale if user hasn't explicitly chosen one
  useEffect(() => {
    if (match?.locale && !localStorage.getItem('userLanguage')) {
      onLocaleChange(match.locale, { explicit: false });
    }
  }, [match?.locale]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const { squadsPublished, stagesPublished, scoresPublished } = match;

  const contextValue = {
    match,
    setMatch,
    announcements,
    setAnnouncements,
    auth,
    setAuth,
    refreshMatch: fetchMatch,
    locale,
    onLocaleChange,
  };

  return (
    <MatchContext.Provider value={contextValue}>
      <Box sx={{ minHeight: '100vh' }}>
        {/* AppBar with match logo + title + nav buttons */}
        <AppBar position="static" className="noprint">
          <Toolbar>
            <Box
              component={Link}
              to={`/${match.alias}`}
              sx={{
                height: 100,
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                minWidth: 0,
              }}
            >
              {match.logo && (
                <Box sx={{
                  backgroundImage: `url('${match.logo}')`,
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  width: 100,
                  height: 100,
                }} />
              )}
              <Typography variant="h6" sx={{ mx: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {match.title}
              </Typography>
            </Box>

            {!isMobile && (
              <>
                <Box sx={{ flexGrow: 1 }} />
                <Button sx={{ textTransform: 'none' }} color="inherit" component={Link} to={`/${match.alias}/squads`} disabled={!squadsPublished}>
                  {t('matches.squads')}
                </Button>
                <Button sx={{ textTransform: 'none' }} color="inherit" component={Link} to={`/${match.alias}/shooters`} disabled={!squadsPublished}>
                  {t('matches.shooters')}
                </Button>
                <Button sx={{ textTransform: 'none' }} color="inherit" component={Link} to={`/${match.alias}/stages`} disabled={!stagesPublished}>
                  {t('matches.stages')}
                </Button>
                {match.registration !== 'closed' && (
                  <Button sx={{ textTransform: 'none' }} color="inherit" component={Link} to={`/${match.alias}/registration`}>
                    {t('matches.registration')}
                  </Button>
                )}
                <Button sx={{ textTransform: 'none' }} color="inherit" component={Link} to={`/${match.alias}/results`} disabled={!scoresPublished}>
                  {t('matches.results')}
                </Button>
              </>
            )}
          </Toolbar>
        </AppBar>

        {/* Hero cover section */}
        <Box
          className="noprint"
          sx={{
            width: '100%',
            height: 'calc(80vh - 100px)',
            backgroundImage: match.cover ? `url('${match.cover}')` : 'none',
            backgroundColor: match.cover ? 'transparent' : '#e0e0e0',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper sx={{
            textAlign: 'center',
            p: { xs: '16px 20px', sm: '24px 48px' },
            background: 'rgba(255, 255, 255, 0.6)',
            maxWidth: { xs: '85%', sm: '50%' },
          }}>
            <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {match.title}
            </Typography>
            {(() => {
              const path = location.pathname.replace(`/${match.alias}`, '').replace(/^\//, '');
              const pageTitles = {
                'shooters': t('matches.shooters'),
                'squads': t('matches.squads'),
                'stages': t('matches.stages'),
                'results': t('matches.results'),
                'registration': t('matches.registration'),
                'shooter-stats': t('results.shooterStats'),
                'compare-shooters': t('results.compareShooters'),
                'score-table': t('matches.results'),
                'flags-podium': t('results.flagsPodium'),
              };
              const pageTitle = pageTitles[path] || pageTitles[path.split('/')[0]];
              const subtitle = pageTitle || match.description;
              return subtitle ? (
                <Typography variant="h6" sx={{ whiteSpace: 'normal' }}>
                  {subtitle}
                </Typography>
              ) : null;
            })()}
          </Paper>
        </Box>

        <Box sx={{ mt: 1.5 }} />

        {/* Main content */}
        <Outlet context={contextValue} />

        <Box sx={{ mb: 3 }} />

        {/* Footer */}
        <Box
          className="noprint"
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 3,
            borderTop: '1px solid rgba(0,0,0,0.2)',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption">
              {t('matchHome.poweredBy')}{' '}
              <Box component="a" href="/" sx={{ color: 'inherit' }}>endofscoring.com</Box>
              {' | '}
              <Box component="a" href="https://docs.google.com/document/d/1Saeek_HSNjO4_yW6FvtkQHOAFplhmxEs1Ctd-UMYOto/edit?usp=sharing" target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit' }}>
                {t('matchHome.termsOfService')}
              </Box>
              {' | '}
              <Box component="a" href="/privacy.html" target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit' }}>
                {t('matchHome.privacyPolicy')}
              </Box>
              {' | '}
            </Typography>
            <Box
              component="span"
              onClick={(e) => setLangAnchor(e.currentTarget)}
              sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}
            >
              <LanguageIcon sx={{ fontSize: 14, mr: 0.25 }} />
              <Typography variant="caption" sx={{ textDecoration: 'underline' }}>
                {(LANGUAGES.find((l) => l.code === locale || l.code.startsWith(locale)) || LANGUAGES[0]).label}
              </Typography>
            </Box>
            <Menu anchorEl={langAnchor} open={Boolean(langAnchor)} onClose={() => setLangAnchor(null)}>
              {LANGUAGES.map((lang) => (
                <MenuItem
                  key={lang.code}
                  selected={locale === lang.code}
                  onClick={() => { onLocaleChange(lang.code); setLangAnchor(null); }}
                >
                  {lang.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem', mr: 1 }}>
            C:{__APP_VERSION__} S:{serverVersion}
          </Typography>
          <Button color="primary" component={Link} to={`/${match.alias}/manage`}>
            <SettingsIcon fontSize="small" />
            &nbsp;
            <Typography variant="caption" sx={{ color: 'inherit' }}>
              {t('matchHome.manageLogin')}
            </Typography>
          </Button>
        </Box>
      </Box>
    </MatchContext.Provider>
  );
}
